import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { numberFormater } from '../../../utils/formater'
import {
  BlockStack,
  Box,
  InlineGrid,
  RangeSlider,
  Select,
  Text,
  Badge,
} from '@shopify/polaris'
import { calculateEstimatedCharge } from './accountHelpers'

/**
 * Reusable Price Estimator Component for Order Count pricing
 * Shows a slider to estimate monthly charges based on order count
 * For free plan users, includes a plan selector to estimate based on paid plans
 */
const PriceEstimator = ({ 
  planForEstimate,
  selectedOrderCount,
  maxSliderValue = 10000,
  isTrialPeriod = false,
  isFreePlan = false,
  availablePlans = [],
  selectedPlan = null,
  onPlanChange = null
}) => {
  const { t } = useTranslation()
  // Format the price display
  const formatPrice = (charge) => {
    if (charge === 0 || charge === '0' || charge === '$0.00') {
      return <Badge tone="success">Free</Badge>
    }
    // Ensure we have a number and format it properly
    const price = typeof charge === 'string' ? parseFloat(charge) : charge
    return `$${price.toFixed(2)}`
  }
  const [currentSliderValue, setCurrentSliderValue] = useState(selectedOrderCount)
  const [estimate, setEstimate] = useState(null)
  
  
  const onCurrentSliderValueChange = (value) => {
    setCurrentSliderValue(value)
    const estimate = calculateEstimatedCharge(value, planForEstimate.steps, planForEstimate.price)
    setEstimate(estimate)
  }
  useEffect(() => {
    setCurrentSliderValue(selectedOrderCount)
    const estimate = calculateEstimatedCharge(selectedOrderCount, planForEstimate.steps, planForEstimate.price)
    setEstimate(estimate)
  }, [selectedOrderCount, planForEstimate])

  // Prepare plan options for the selector
  const planOptions = availablePlans.map(plan => ({
    label: `${plan.name || plan.slug} - $${parseFloat(plan.price || 0).toFixed(2)}/month base`,
    value: plan.id ? String(plan.id) : plan.slug
  }))

  // Get description text based on context
  const getDescriptionText = () => {
    if (isFreePlan && selectedPlan) {
      return t('SettingsPage.Sections.Account.Card.OrderCount.estimator.description', {planName: selectedPlan.name || selectedPlan.slug || 'selected'})
    }
    if (isTrialPeriod) {
      return t('SettingsPage.Sections.Account.Card.OrderCount.estimator.trialDescription')
    }
    return t('SettingsPage.Sections.Account.Card.OrderCount.estimator.paidDescription')
  }

  return (
    <BlockStack gap="250">
      <Text variant="headingMd">{t('SettingsPage.Sections.Account.Card.OrderCount.estimator.title')}</Text>
      <Text variant="bodySm" tone="subdued">
        {getDescriptionText()}
      </Text>
      
      {/* Show plan selector for free plan users */}
      {isFreePlan && availablePlans.length > 0 && (
        <Box paddingBlockEnd="300">
          <Select
            label={t('SettingsPage.Sections.Account.Card.OrderCount.estimator.selectPlanLabel')}
            labelHidden={false}
            options={planOptions}
            value={selectedPlan?.id ? String(selectedPlan.id) : (selectedPlan?.slug || '')}
            onChange={onPlanChange}
            helpText={t('SettingsPage.Sections.Account.Card.OrderCount.estimator.selectPlanHelpText')}
          />
        </Box>
      )}
      <Box paddingBlock="400">
        <BlockStack>
          <RangeSlider
            label={t('SettingsPage.Sections.Account.Card.OrderCount.estimator.estimatedOrderCountLabel')}
            labelHidden
            value={currentSliderValue}
            min={0}
            max={maxSliderValue}
            step={100}
            onChange={(value) => {
              onCurrentSliderValueChange(value)
            }}
            output
          />
          
          <InlineGrid columns="1fr 1fr" gap="300">
            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.OrderCount.estimator.selectedOrdersLabel')}:</Text>
              <Text variant="bodyLg" fontWeight="semibold">
                {numberFormater(currentSliderValue)}
              </Text>
            </BlockStack>
            
            <BlockStack gap="100">
              <Text tone="subdued">{t('SettingsPage.Sections.Account.Card.OrderCount.estimator.estimatedMonthlyPriceLabel')}:</Text>
              <Text variant="bodyLg" fontWeight="semibold">
                {formatPrice(estimate?.estimatedCharge || 0)}
              </Text>
            </BlockStack>
          </InlineGrid>
        </BlockStack>
      </Box>
    </BlockStack>
  )
}

export default PriceEstimator